import { defineStore } from 'pinia'
import {
  PostMarkSiteDomainDetails,
  SFTPSSHCreds,
  SiteBackupsInfo,
  SiteGeneralInfo,
  SiteInfo,
  SiteResourceInfo,
  SiteWpCore,
  SiteWpPlugin,
  SiteWpTheme,
} from '@/models/site'
import { SiteSchedules } from '@/models/siteSchedules'
import * as SiteService from '@/services/site/siteService'

/**
 * Site store state interface
 * Manages all site-related data including selected site details, WordPress components, and domain information
 */
interface SiteStoreState {
  /** Array of all user sites */
  sites: SiteGeneralInfo[]
  /** Currently selected site with all its details */
  selectedSite: {
    /** ID of the currently selected site */
    siteId?: number
    /** Detailed information about the selected site */
    details: {
      /** General site information (name, domain, plan, etc.) */
      general?: SiteGeneralInfo
      /** Server and technical information */
      info?: SiteInfo
      /** Resource usage information */
      resources?: SiteResourceInfo
      /** SFTP/SSH credentials for site access */
      sftpSshCreds?: SFTPSSHCreds
      /** Backup information for file system and database */
      backups: SiteBackupsInfo
      /** Database login URL */
      dbLoginUrl?: string
      /** PageSpeed Insights data */
      psi?: any
    }
    /** WordPress-specific information */
    wordpress: {
      /** WordPress admin login URL */
      adminLoginUrl?: string
      /** Installed themes */
      themes: SiteWpTheme[]
      /** WordPress core versions */
      cores: SiteWpCore[]
      /** Installed plugins */
      plugins: SiteWpPlugin[]
    }
  }
  /** Postmark domain details for email configuration */
  currentSitePostmarkDomainDetails?: PostMarkSiteDomainDetails
  /** Site schedule details for automated tasks */
  selectedSiteScheduleDetails?: SiteSchedules
  /** Total count of user sites including deleted ones */
  totalSitesCountOfUserIncludingDeleted?: number
}

export const useSitesStore = defineStore('sites', {
  state: (): SiteStoreState => {
    return {
      sites: [],
      selectedSite: {
        siteId: undefined,
        details: {
          general: undefined,
          info: undefined,
          resources: undefined,
          sftpSshCreds: undefined,
          dbLoginUrl: undefined,
          backups: {
            fileSystem: [],
            databaseSystem: [],
          },
        },
        wordpress: {
          adminLoginUrl: undefined,
          themes: [],
          cores: [],
          plugins: [],
        },
      },
      currentSitePostmarkDomainDetails: undefined,
      selectedSiteScheduleDetails: undefined,
      totalSitesCountOfUserIncludingDeleted: undefined,
    }
  },
  getters: {
    siteById: (state) => (id: string) => {
      return state.sites.find((site: any) => site.id === id)
    },
    selectedSiteDetails: (state) => {
      return state.selectedSite?.details
    },
    selectedSiteWordpressDetails: (state) => {
      return state.selectedSite?.wordpress
    },
    searchedSites: (state) => {
      return (searchQuery: string): SiteGeneralInfo[] | undefined => {
        return state.sites
      }
    },
    selectedSiteDomains: (state) => {
      return state.selectedSite?.details?.general?.domain || []
    },
    selectedSiteId: (state) => {
      return state.selectedSite?.siteId
    },
    getCurrentSitePostmarkDomainDetails: (state) => {
      return state.currentSitePostmarkDomainDetails
    },
    getSelectedSiteScheduleDetails: (state) => {
      return state.selectedSiteScheduleDetails
    },
    getSelectedSiteScheduleCover: (state) => {
      return state.selectedSiteScheduleDetails?.site?.siteReportingOptions
        ?.report_cover_url
    },
  },
  actions: {
    setSites(sites: SiteGeneralInfo[]) {
      this.sites = sites
    },
    setSelectedSiteId(siteId?: number) {
      this.selectedSite.siteId = siteId
    },
    setCurrentSitePostmarkDomainDetails(
      domainDetails: PostMarkSiteDomainDetails,
    ) {
      this.currentSitePostmarkDomainDetails = domainDetails
    },
    removeDomain(id: number) {
      this.selectedSite!.details!.general!.domain =
        this.selectedSite?.details?.general?.domain?.filter(
          (domain) => domain.id !== id,
        )
    },
    toggleSiteEdgeCache(status: boolean) {
      if (!this.selectedSite?.details) {
        return
      }
      this.selectedSite = {
        ...this.selectedSite,
        details: {
          ...this.selectedSite.details,
          info: {
            ...(this.selectedSite.details.info || {}),
            edgeCache: status,
          },
        },
      }
    },
    setSelectedSiteScheduleDetails(scheduleDetails: SiteSchedules) {
      this.selectedSiteScheduleDetails = scheduleDetails
    },
    clearSites() {
      this.sites = []
    },

    /**
     * Clear all selected site related data
     * Resets the selected site to initial state
     */
    clearSelectedSiteData() {
      this.selectedSite = {
        siteId: undefined,
        details: {
          general: undefined,
          info: undefined,
          resources: undefined,
          sftpSshCreds: undefined,
          dbLoginUrl: undefined,
          backups: {
            fileSystem: [],
            databaseSystem: [],
          },
        },
        wordpress: {
          adminLoginUrl: undefined,
          themes: [],
          cores: [],
          plugins: [],
        },
      }
      this.currentSitePostmarkDomainDetails = undefined
      this.selectedSiteScheduleDetails = undefined
    },

    /**
     * Fetch site information and store in state
     * @param forceFetch - Whether to force fetch even if data exists
     * @param siteId - The site ID to fetch info for
     */
    async fetchSiteInfo(forceFetch = false, siteId?: number) {
      const targetSiteId = siteId || this.selectedSite.siteId
      if (!targetSiteId) return

      // Check if data already exists and not forcing fetch
      if (!forceFetch && this.selectedSite.details.info) {
        return this.selectedSite.details.info
      }

      try {
        const siteInfo = await SiteService.getSiteInfo(1, Number(targetSiteId))
        this.selectedSite.details.info = siteInfo
        return siteInfo
      } catch (error) {
        console.error('Error fetching site info:', error)
        throw error
      }
    },

    /**
     * Fetch SFTP/SSH credentials and store in state
     * @param forceFetch - Whether to force fetch even if data exists
     * @param siteId - The site ID to fetch credentials for
     */
    async fetchSftpCredentials(forceFetch = false, siteId?: number) {
      const targetSiteId = siteId || this.selectedSite.siteId
      if (!targetSiteId) return

      // Check if data already exists and not forcing fetch
      if (!forceFetch && this.selectedSite.details.sftpSshCreds) {
        return this.selectedSite.details.sftpSshCreds
      }

      try {
        const credentials = await SiteService.getSiteSftpCreds(
          Number(targetSiteId),
        )
        this.selectedSite.details.sftpSshCreds = credentials
        return credentials
      } catch (error) {
        console.error('Error fetching SFTP credentials:', error)
        throw error
      }
    },

    /**
     * Fetch general site details and store in state
     * @param forceFetch - Whether to force fetch even if data exists
     * @param siteId - The site ID to fetch details for
     */
    async fetchGeneralSiteDetails(forceFetch = false, siteId?: number) {
      const targetSiteId = siteId || this.selectedSite.siteId
      if (!targetSiteId) return

      // Check if data already exists and not forcing fetch
      if (!forceFetch && this.selectedSite.details.general) {
        return this.selectedSite.details.general
      }

      try {
        const site = await SiteService.getGeneralSiteDetail(
          Number(targetSiteId),
        )

        // Map to store with proper field mapping
        this.selectedSite.details.general = {
          ...site,
          stripeAddOnsPriceIds: (site as any).stripe_addon_ids,
          stripePriceId: (site as any).stripe_price_id,
          removeOnSubscriptionRenewal: (site as any)
            .remove_on_subscription_renewal,
        }

        return this.selectedSite.details.general
      } catch (error) {
        console.error('Error fetching general site details:', error)
        throw error
      }
    },

    /**
     * Fetch site backups and store in state
     * @param forceFetch - Whether to force fetch even if data exists
     * @param siteId - The site ID to fetch backups for
     */
    async fetchSiteBackups(forceFetch = false, siteId?: number) {
      const targetSiteId = siteId || this.selectedSite.siteId
      if (!targetSiteId) return

      // Check if data already exists and not forcing fetch
      const existingBackups = this.selectedSite.details?.backups
      if (
        !forceFetch &&
        existingBackups &&
        ((existingBackups.fileSystem?.length ?? 0) > 0 ||
          (existingBackups.databaseSystem?.length ?? 0) > 0)
      ) {
        return existingBackups
      }

      try {
        const backups = await SiteService.getSiteBackups(Number(targetSiteId))

        // Check if it's a Nestify site and handle accordingly
        const isNestifySite =
          this.selectedSite.details.general?.provider?.slug === 'nestify'

        if (isNestifySite) {
          this.selectedSite.details.backups = {
            combinedBackups: backups,
          } as any
        } else {
          this.selectedSite.details.backups = backups ?? {
            fileSystem: [],
            databaseSystem: [],
          }
        }

        return this.selectedSite.details.backups
      } catch (error) {
        console.error('Error fetching site backups:', error)
        throw error
      }
    },
  },
})
